import { useEffect, useState } from "react";
import { CreditCard, Loader2 } from "lucide-react";

const STATUSES = ["trialing", "active", "past_due", "suspended", "cancelled"];

export const AdminClinicPlan = ({ clinicId, adminApi }) => {
  const [plans, setPlans] = useState([]);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState("");

  const load = async () => {
    const [{ data: catalog }, { data: current }] = await Promise.all([
      adminApi("get", "/admin/plans"),
      adminApi("get", `/admin/clinics/${clinicId}/plan`),
    ]);
    setPlans(catalog.plans);
    setData(current);
  };

  useEffect(() => { setData(null); load(); /* eslint-disable-next-line */ }, [clinicId]);

  const save = async (payload) => {
    setSaving(true);
    try {
      await adminApi("put", `/admin/clinics/${clinicId}/plan`, payload);
      await load();
      setSaved("Saved");
      setTimeout(() => setSaved(""), 2000);
    } catch (e) {
      setSaved(e.response?.data?.detail || "Failed");
    }
    setSaving(false);
  };

  if (!data) {
    return (
      <div className="border-t border-white/[0.06] pt-3 mb-4 flex items-center gap-2 text-[10px] text-white/40">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading plan…
      </div>
    );
  }

  const sub = data.subscription;
  return (
    <div className="border-t border-white/[0.06] pt-3 mb-4" data-testid="admin-clinic-plan">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[9px] text-white/30 uppercase tracking-wider flex items-center gap-1.5">
          <CreditCard className="w-3 h-3" /> Plan &amp; limits
        </p>
        {saving ? <Loader2 className="w-3 h-3 text-[#7EBDBC] animate-spin" />
          : saved ? <span className="text-[9px] text-[#7EBDBC]">{saved}</span> : null}
      </div>

      <div className="space-y-2.5">
        {data.pending_request && (
          <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 px-2.5 py-2 text-[10px] text-amber-200"
            data-testid="admin-plan-pending-request">
            Upgrade requested: {data.pending_request.requested_plan_name}
            {data.pending_request.requested_by_name ? ` by ${data.pending_request.requested_by_name}` : ""}.
            Changing the plan below marks it applied.
          </div>
        )}

        <label className="block">
          <span className="text-[9px] text-white/40">Plan</span>
          <select value={sub.plan_id} disabled={saving} data-testid="admin-plan-select"
            onChange={(e) => save({ plan_id: e.target.value })}
            className="w-full mt-1 bg-white/[0.04] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white">
            {plans.map((p) => (
              <option key={p.id} value={p.id} className="bg-[#131E1E]">
                {p.name}{p.public ? "" : " · internal"}
                {p.price_gbp === null ? " · custom" : ` · £${p.price_gbp}`}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-[9px] text-white/40">Subscription status</span>
          <select value={sub.subscription_status} disabled={saving} data-testid="admin-plan-status-select"
            onChange={(e) => save({ subscription_status: e.target.value })}
            className="w-full mt-1 bg-white/[0.04] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white">
            {STATUSES.map((s) => <option key={s} value={s} className="bg-[#131E1E]">{s}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-[9px] text-white/40">Trial end (ISO date, blank to clear)</span>
          <input type="date" disabled={saving} data-testid="admin-plan-trial-end"
            defaultValue={(sub.trial_end || "").slice(0, 10)}
            onBlur={(e) => e.target.value && save({ trial_end: `${e.target.value}T23:59:59+00:00` })}
            className="w-full mt-1 bg-white/[0.04] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white" />
        </label>

        <div className="grid grid-cols-3 gap-2">
          {[
            ["practitioner_limit", "Seats"],
            ["ai_notes_limit", "AI notes"],
            ["ai_minutes_limit", "AI mins"],
          ].map(([key, label]) => (
            <label key={key} className="block">
              <span className="text-[9px] text-white/40">{label}</span>
              <input type="number" min="0" disabled={saving} data-testid={`admin-plan-${key}`}
                defaultValue={sub[key] ?? ""} placeholder="∞"
                onBlur={(e) => e.target.value !== "" && Number(e.target.value) !== sub[key]
                  && save({ [key]: Number(e.target.value) })}
                className="w-full mt-1 bg-white/[0.04] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white" />
            </label>
          ))}
        </div>

        <button onClick={() => save({ ai_documentation_enabled: !sub.ai_documentation_enabled })}
          disabled={saving} data-testid="admin-plan-ai-toggle"
          className={`w-full text-[10px] py-2 rounded-lg border transition-colors ${
            sub.ai_documentation_enabled
              ? "border-[#205B5A] bg-[#205B5A]/20 text-[#7EBDBC]"
              : "border-white/10 bg-white/[0.04] text-white/50"}`}>
          AI documentation: {sub.ai_documentation_enabled ? "enabled" : "disabled"} (click to toggle)
        </button>

        <div className="text-[9px] text-white/40 space-y-0.5 pt-1">
          <p data-testid="admin-plan-seat-usage">
            Practitioners: {data.practitioners.used} of {data.practitioners.limit ?? "∞"}
          </p>
          <p data-testid="admin-plan-ai-usage">
            AI this month: {data.ai.usage.notes_used} of {data.ai.usage.notes_limit ?? "∞"} notes ·{" "}
            {data.ai.usage.minutes_used} of {data.ai.usage.minutes_limit ?? "∞"} mins
          </p>
          <p className={data.ai.allowed ? "text-[#7EBDBC]" : "text-amber-400"} data-testid="admin-plan-ai-access">
            AI access: {data.ai.allowed ? "allowed" : `blocked (${data.ai.code})`}
          </p>
          <p>Note templates: {data.ai.note_templates}</p>
        </div>
      </div>
    </div>
  );
};
